import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import artistApi from '@/lib/artistApi';
import { sendFirebasePasswordReset } from '@/lib/firebasePasswordReset';

const ArtistAuthContext = createContext(null);

export const ArtistAuthProvider = ({ children }) => {
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshArtist = useCallback(async () => {
    try {
      const response = await artistApi.get('/artist-auth/me');
      setArtist(response.data);
      return response.data;
    } catch (error) {
      setArtist(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshArtist();
  }, [refreshArtist]);

  const logout = useCallback(async () => {
    try {
      await artistApi.post('/artist-auth/logout');
    } finally {
      setArtist(null);
      window.dispatchEvent(new CustomEvent('learnAuthChanged', { detail: { authenticated: false } }));
    }
  }, []);

  const updateArtist = useCallback(async (payload) => {
    const response = await artistApi.put('/artist-auth/me', payload);
    setArtist(response.data);
    return response.data;
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const email = artist?.email?.trim();
    if (!email) {
      throw new Error('Artist email is missing for password change.');
    }
    try {
      const repairResponse = await artistApi.post('/artist-auth/repair-password-sync', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      try {
        await sendFirebasePasswordReset(email);
        return {
          ...repairResponse.data,
          message: 'Password changed successfully. A confirmation email has been sent. Please check your inbox and spam folder.',
        };
      } catch (resetError) {
        return {
          ...repairResponse.data,
          message: 'Password changed successfully. A confirmation email has been sent.',
        };
      }
    } catch (error) {
      throw error;
    }
  }, [artist?.email]);

  const value = useMemo(() => ({
    artist,
    loading,
    hasSession: Boolean(artist),
    isAuthenticated: Boolean(artist),
    setArtist,
    refreshArtist,
    logout,
    updateArtist,
    changePassword,
  }), [artist, loading, refreshArtist, logout, updateArtist, changePassword]);

  return <ArtistAuthContext.Provider value={value}>{children}</ArtistAuthContext.Provider>;
};

export const useArtistAuth = () => {
  const context = useContext(ArtistAuthContext);
  if (!context) {
    throw new Error('useArtistAuth must be used within an ArtistAuthProvider');
  }
  return context;
};
